import { isCapacitorNative, isNativeKioskShell } from "./capacitor";
import { isKiosk, isAuthedAsDevice } from "./auth";

const OVERRIDE_KEY = "sugalarm.webAccess";

/**
 * Cycle 281: web-browser access gate.
 *
 * During Early Access we don't let the general public sign in from a plain
 * web browser (web access is subscription-gated, and that's not live yet) —
 * but the **native iOS/Android app and any kiosk always pass through**, since
 * those are the surfaces the alarm actually runs on.
 *
 * FAIL-OPEN by design: any app / kiosk / device-session signal, an explicit
 * `?kiosk=1` (a booting kiosk) or `?web=1` (operator/subscriber override),
 * or the stored override lets the client through. It returns `false` (block)
 * ONLY for a client that shows NONE of those — a plain web browser.
 *
 * This is a purely client-side SOFT gate: it hides the login UI on the web,
 * but never touches the auth endpoints, so the kiosk + app sign-in paths can
 * never be affected by it. It also only gates the signed-OUT state (new
 * logins) — an already-signed-in session is untouched.
 */
export function webAccessAllowed(): boolean {
  if (typeof window === "undefined") return true; // SSR / no window → fail open
  // Native app or kiosk hardware — always allowed.
  if (
    isCapacitorNative() ||
    isNativeKioskShell() ||
    isKiosk() ||
    isAuthedAsDevice()
  ) {
    return true;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    // A fresh kiosk boots at `?kiosk=1` before its flag persists — never block.
    if (params.get("kiosk") === "1") return true;
    // Operator / future-subscriber override, persisted on this browser.
    if (params.get("web") === "1") {
      localStorage.setItem(OVERRIDE_KEY, "1");
      return true;
    }
    if (localStorage.getItem(OVERRIDE_KEY) === "1") return true;
  } catch {
    return true; // storage blocked (private mode, etc.) → fail open
  }
  return false;
}
